import { Fragment as ReactFragment, createContext, useContext } from 'react';
import { cx } from '../../lib/cx';
import { CodeBlock } from './CodeBlock';
import { isRecord } from '../../lib/guards';

/**
 * How an image node's attachment id becomes a URL that works right now.
 *
 * A context rather than a prop, because the document renders recursively —
 * blocks inside list items inside blocks — and threading a resolver through
 * every level would put it in the signature of code that has nothing to do
 * with images. It is ambient for one rendered document, which is exactly what
 * it is.
 *
 * Undefined by default, and that is a legitimate state rather than a bug: a
 * surface that renders a body without loading its attachments (a search
 * result, a notification preview) simply shows "Image unavailable" instead of
 * a broken one.
 */
const ResolveAttachmentUrl = createContext<((attachmentId: string) => string | undefined) | undefined>(
  undefined,
);

/**
 * Renders a message's ProseMirror document — the same JSON shape
 * `apps/api/.../message-body.ts` validates on the way in.
 *
 * This walks the tree and maps each node to a React element rather than
 * building an HTML string and using `dangerouslySetInnerHTML`. That
 * distinction is the point: React escapes every piece of text it renders, and
 * the only place raw text ever reaches the DOM as an *attribute* rather than
 * text content is a link's `href` — which is re-validated below with the same
 * http(s)-only rule the server enforces, rather than trusted because "the
 * server already checked it". Two independent checks are cheap insurance
 * against the day the two drift.
 *
 * `body` is `unknown` (see `Message.body` in `@rundown/types`), so every shape
 * assumption here is a runtime check, not a cast. A node this component does
 * not recognise (which should not happen, since the API already rejected
 * anything outside the allowlist before it was ever stored) is skipped rather
 * than crashing the whole conversation.
 */

function isSafeHref(href: string): boolean {
  try {
    const url = new URL(href);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function textOf(node: Record<string, unknown>): string {
  return typeof node['text'] === 'string' ? node['text'] : '';
}

function MarkedText({ node }: { node: Record<string, unknown> }) {
  const text = textOf(node);
  const marks = Array.isArray(node['marks']) ? node['marks'] : [];

  let element: React.ReactNode = text;
  for (const mark of marks) {
    if (!isRecord(mark)) continue;
    switch (mark['type']) {
      case 'bold':
        element = <strong>{element}</strong>;
        break;
      case 'italic':
        element = <em>{element}</em>;
        break;
      case 'code':
        element = <code className="rounded bg-surface-3 px-1 py-0.5 font-mono fs-12">{element}</code>;
        break;
      case 'link': {
        const attrs = mark['attrs'];
        const href = isRecord(attrs) && typeof attrs['href'] === 'string' ? attrs['href'] : '';
        if (isSafeHref(href)) {
          element = (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-accent underline">
              {element}
            </a>
          );
        }
        break;
      }
      default:
        break;
    }
  }
  return <>{element}</>;
}

function InlineNodeView({ node, index }: { node: unknown; index: number }) {
  if (!isRecord(node)) return null;
  switch (node['type']) {
    case 'text':
      return <MarkedText key={index} node={node} />;
    case 'hardBreak':
      return <br key={index} />;
    case 'mention': {
      const attrs = node['attrs'];
      const label = isRecord(attrs) && typeof attrs['label'] === 'string' ? attrs['label'] : '';
      return (
        <span
          key={index}
          className="rounded-full bg-accent-tint px-1.5 py-px font-medium whitespace-nowrap text-accent"
        >
          @{label}
        </span>
      );
    }
    default:
      return null;
  }
}

function inlineContent(node: Record<string, unknown>): React.ReactNode {
  const content = node['content'];
  if (!Array.isArray(content)) return null;
  return content.map((child, index) => <InlineNodeView key={index} node={child} index={index} />);
}

function ListItemView({ node, index }: { node: unknown; index: number }) {
  if (!isRecord(node) || !Array.isArray(node['content'])) return null;
  return (
    <li key={index}>
      {node['content'].map((child, childIndex) => (
        <BlockNodeView key={childIndex} node={child} index={childIndex} inline />
      ))}
    </li>
  );
}

/**
 * `inline`: a list item's own paragraph should not add the paragraph's usual
 * block spacing (`<p>` elsewhere gets `whitespace-pre-line` block treatment)
 * — it is one line inside an `<li>`, not a standalone paragraph.
 */
/**
 * An image block: the attachment id resolved to a URL that works now.
 *
 * `src` is the editor's transient blob URL and is only ever present while the
 * author's own tab is still showing it — the stored document never carries
 * one (see imageNode.ts). So a resolved URL wins, the blob is the fallback
 * during authoring, and neither means the image is gone.
 */
function ImageNodeView({ node }: { node: Record<string, unknown> }) {
  const resolve = useContext(ResolveAttachmentUrl);
  const attrs = isRecord(node['attrs']) ? node['attrs'] : {};
  const attachmentId = typeof attrs['attachmentId'] === 'string' ? attrs['attachmentId'] : null;
  const alt = typeof attrs['alt'] === 'string' ? attrs['alt'] : '';
  const src =
    (attachmentId !== null ? resolve?.(attachmentId) : undefined) ??
    (typeof attrs['src'] === 'string' ? attrs['src'] : undefined);

  if (src === undefined) {
    return (
      <p className="fs-12 text-faint italic">
        {alt === '' ? 'Image unavailable' : `Image unavailable — ${alt}`}
      </p>
    );
  }

  return (
    <img src={src} alt={alt} className="my-1 max-h-[24rem] max-w-full rounded-[8px] border border-line" />
  );
}

function BlockNodeView({ node, index, inline = false }: { node: unknown; index: number; inline?: boolean }) {
  if (!isRecord(node)) return null;

  switch (node['type']) {
    case 'paragraph':
      return inline ? (
        <ReactFragment key={index}>{inlineContent(node)}</ReactFragment>
      ) : (
        <p key={index} className="leading-relaxed whitespace-pre-line">
          {inlineContent(node)}
        </p>
      );
    case 'codeBlock': {
      const content = Array.isArray(node['content']) ? node['content'] : [];
      const text = content.map((child) => (isRecord(child) ? textOf(child) : '')).join('');
      // `language` is whatever the editor stored, and may be absent — a pasted
      // block usually has none, and CodeBlock falls back to detection.
      const attrs = isRecord(node['attrs']) ? node['attrs'] : {};
      const language = typeof attrs['language'] === 'string' ? attrs['language'] : null;
      return <CodeBlock key={index} text={text} language={language} />;
    }
    case 'image': {
      return <ImageNodeView key={index} node={node} />;
    }
    case 'bulletList': {
      const items = Array.isArray(node['content']) ? node['content'] : [];
      return (
        <ul key={index} className={cx('list-disc space-y-0.5 pl-5', !inline && 'my-1')}>
          {items.map((item, itemIndex) => (
            <ListItemView key={itemIndex} node={item} index={itemIndex} />
          ))}
        </ul>
      );
    }
    case 'orderedList': {
      const items = Array.isArray(node['content']) ? node['content'] : [];
      const attrs = node['attrs'];
      const start = isRecord(attrs) && typeof attrs['start'] === 'number' ? attrs['start'] : 1;
      return (
        <ol key={index} start={start} className={cx('list-decimal space-y-0.5 pl-5', !inline && 'my-1')}>
          {items.map((item, itemIndex) => (
            <ListItemView key={itemIndex} node={item} index={itemIndex} />
          ))}
        </ol>
      );
    }
    default:
      return null;
  }
}

/**
 * `fallbackText` is not defensive padding — it is what every message written
 * before the Tiptap composer existed needs in order to render at all.
 *
 * Those rows hold `{ type: 'doc', text: '…' }`: a doc with no `content` array,
 * because the plain-text composer stored the string alongside a nominal doc
 * wrapper. Walking that tree produces nothing, so without this the entire
 * history of every conversation silently went blank the moment rich text
 * shipped — the text was still in `body_text`, still indexed, still returned
 * over the wire, and simply not drawn.
 *
 * Rendering `bodyText` instead is safe in exactly the way the tree walk is:
 * React escapes it as text content, and it never reaches the DOM as an
 * attribute. A backfill migration could convert the old rows properly, but
 * this has to be correct whether or not that ever happens.
 */
export function MessageBodyView({
  body,
  fallbackText = '',
  resolveAttachmentUrl,
}: {
  body: unknown;
  fallbackText?: string;
  /** Turns an image node's attachment id into a URL that works right now. */
  resolveAttachmentUrl?: (attachmentId: string) => string | undefined;
}): React.ReactElement | null {
  const isRenderableDoc = isRecord(body) && body['type'] === 'doc' && Array.isArray(body['content']);

  if (!isRenderableDoc) {
    if (fallbackText.trim() === '') return null;
    return <div className="fs-13 leading-relaxed whitespace-pre-line text-ink-soft">{fallbackText}</div>;
  }

  return (
    <ResolveAttachmentUrl.Provider value={resolveAttachmentUrl}>
      <div className="fs-13 leading-relaxed text-ink-soft">
        {(body['content'] as unknown[]).map((block, index) => (
          <BlockNodeView key={index} node={block} index={index} />
        ))}
      </div>
    </ResolveAttachmentUrl.Provider>
  );
}
